/**
 * Internal traffic: admins, developers, test robots. Their hits still go to
 * GA4 (GTM debugging needs them), but every push carries
 * `traffic_type: "internal"`, so the GA4 internal-traffic data filter drops
 * them from reports, audiences and Ads conversions.
 *
 * Internal when any of:
 *  - the device has ever been signed in as an admin (core list or the dynamic
 *    one, shared/config/admin) — remembered in localStorage, so the admin's
 *    own logged-out visits and "log in as" sessions stay internal too;
 *  - a "log in as" session is open (its state / admin backup keys) — checked
 *    directly too, since the remembered mark may be missing (cleared storage,
 *    the dynamic list resolved late); it then marks the device for good;
 *  - `?no_analytics=1` was opened once on this device (remembered;
 *    `?no_analytics=0` clears it) — for owner checks and Playwright runs;
 *  - localhost / 127.0.0.1 / a *.dev host (previews, INT server);
 *  - navigator.webdriver (headless browsers, automation).
 *
 * public/index.html repeats the cheap rules (the INTERNAL_KEY flag,
 * ?no_analytics=1/0, the "log in as" keys, webdriver, any host but
 * torsunov.com) before GTM loads and pushes `{traffic_type: "internal"}`, so
 * the Google tag's own hits are marked too (GTM_NOTES.md). The admin e-mail
 * check stays here; an admin's sign-in sets INTERNAL_KEY, which the page
 * then reads on every later load. internal.test.ts keeps the two in step.
 */
import { ADMIN_EMAILS, resolveIsAdmin } from "@/shared/config/admin";
import { IMPERSONATION_BACKUP_KEY, IMPERSONATION_STATE_KEY } from "@/shared/config/localstorage";

export const INTERNAL_KEY = "analytics_internal";
export const NO_ANALYTICS_PARAM = "no_analytics";

const readFlag = (): boolean => {
    try {
        return localStorage.getItem(INTERNAL_KEY) === "1";
    } catch {
        return false;
    }
};

const writeFlag = (on: boolean): void => {
    try {
        if (on) localStorage.setItem(INTERNAL_KEY, "1");
        else localStorage.removeItem(INTERNAL_KEY);
    } catch {
        // storage blocked — the host / webdriver rules still apply
    }
};

export const isInternalHost = (hostname: string): boolean => {
    const h = (hostname || "").toLowerCase();
    return (
        h === "localhost" ||
        h === "127.0.0.1" ||
        h === "[::1]" ||
        h.endsWith(".localhost") ||
        h.endsWith(".dev") ||
        h.includes(".dev.")
    );
};

const signedInEmail = (): string => {
    try {
        return (localStorage.getItem("site_user_email") || "").trim().toLowerCase();
    } catch {
        return "";
    }
};

// Only an admin can open "log in as"; while it is open, site_user_email is the
// TARGET's address, so the email check below would read it as real traffic.
const impersonationOpen = (): boolean => {
    try {
        return !!(localStorage.getItem(IMPERSONATION_STATE_KEY) || localStorage.getItem(IMPERSONATION_BACKUP_KEY));
    } catch {
        return false;
    }
};

export const isInternalTraffic = (): boolean => {
    if (readFlag()) return true;
    if (impersonationOpen()) {
        writeFlag(true);
        return true;
    }
    try {
        if (isInternalHost(window.location.hostname)) return true;
        if (typeof navigator !== "undefined" && navigator.webdriver) return true;
    } catch {
        // no window (tests outside jsdom)
    }
    const email = signedInEmail();
    return !!email && ADMIN_EMAILS.includes(email);
};

/** `?no_analytics=1` marks this device internal for good, `?no_analytics=0` unmarks it. */
export const captureNoAnalyticsFlag = (search: string): void => {
    let v: string | null = null;
    try {
        v = new URLSearchParams(search || "").get(NO_ANALYTICS_PARAM);
    } catch {
        return;
    }
    if (v === "1" || v === "true") writeFlag(true);
    else if (v === "0" || v === "false") writeFlag(false);
};

/**
 * After a sign-in: an admin (core or dynamic list) marks the device internal.
 * A non-admin never clears the mark — an admin's device stays internal.
 */
export const noteSignedInUser = async (identifier: string | null | undefined): Promise<void> => {
    const id = (identifier || "").trim().toLowerCase();
    if (!id) return;
    if (ADMIN_EMAILS.includes(id)) {
        writeFlag(true);
        return;
    }
    try {
        if (await resolveIsAdmin(id)) writeFlag(true);
    } catch {
        // the list is unreachable — the core list above already ran
    }
};
